'use client';

import { c as _c } from "react/compiler-runtime";
import type { db } from '@funkey/api/src';
import { atom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Jotai_AtomsHydrator } from '~/modules/jotai';

/** Catalog filter rules for the active portal — mirrors the
 * `bizz.activityFilters` column plus the auto-add flag. `null`-valued
 * allowlists (`rootCategoryIDs` / `activityRegionIDs`) mean "no constraint =
 * everything allowed". */
export type ActivityFilterRules = Pick<typeof db.businesses.$inferSelect, 'activityFilters' | 'automaticallyAddNewActivitiesToSelection'>;

/** Default for portals without per-tenant rules (WEB / PARTNER / ADMIN):
 * nothing is constrained. The BIZZ subtree overrides this via
 * {@link ActivityFilterRulesProvider}. */
const NO_RULES: ActivityFilterRules = {
  activityFilters: {
    rootCategoryIDs: null,
    activityRegionIDs: null,
    budget: {
      min: null,
      max: null
    },
    participants: {
      min: null,
      max: null
    },
    groupIDs: {
      mode: 'EXCLUSIVE',
      IDs: null
    }
  },
  automaticallyAddNewActivitiesToSelection: false
};

/** Single source of truth the activity filter UI reads (browse hides
 * rule-disabled regions/categories; edit mode renders the full universe and
 * uses these as the pressed-state). Defaults to {@link NO_RULES}. */
export const ACTIVITY_FILTER_RULES = atom<ActivityFilterRules>(NO_RULES);

// Hoisted for stable identity: `ScopeProvider` re-creates its scope when the
// `atoms` array reference changes, which would drop the hydrated/edited value.
const SCOPED_ATOMS = [ACTIVITY_FILTER_RULES];

/** Per-tenant override carrier for the BIZZ portal. Scopes **only**
 * {@link ACTIVITY_FILTER_RULES} — not the whole atom universe, as a fresh
 * `createStore` would — so every other global atom in the BIZZ subtree still
 * resolves against the app's store. Key this on `bizz.id` at the call site: the
 * scoped copy starts at the atom default and the hydrator seeds it once per
 * mount, so a fresh mount per business re-seeds on client nav and keeps
 * concurrent SSR requests isolated. */
export const ActivityFilterRulesProvider: React.FC<React.PropsWithChildren<{
  rules: ActivityFilterRules;
}>> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.rules) {
    t0 = [[ACTIVITY_FILTER_RULES, props.rules]];
    $[0] = props.rules;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.children || $[3] !== t0) {
    t1 = <ScopeProvider atoms={SCOPED_ATOMS}><Jotai_AtomsHydrator values={t0}>{props.children}</Jotai_AtomsHydrator></ScopeProvider>;
    $[2] = props.children;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};